import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAsk } from '../AskContext';

const CONDITIONS = ['FactoryNew', 'NewSurplus', 'DistributorStock', 'NewOldStock', 'Other'];
const LT_TYPES = ['ExStock', 'ARO', 'Production', 'TBD', 'SubjectToPriorSale'];
const COC_TYPES = ['Manufacturer', 'Distributor', 'FAA_8130_3', 'EASA_Form_1', 'JAA_Form_1', 'Unknown'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'SGD', 'AED', 'JPY', 'CHF', 'CAD', 'AUD'];
const TRI = ['Unknown', 'Yes', 'No'];

/**
 * Does this RFQ line impose any aerospace requirement (#16)? The answer fields
 * are only rendered when it does — showing eleven more inputs on every line would
 * bury the price, and an answer to a question nobody asked is noise.
 */
function needsAero(line) {
  return !!(
    line.dfarsRequired || line.specialtyMetalsCompliance ||
    (line.approvedCountriesOfOrigin || []).length ||
    line.itarControlled || line.earControlled ||
    line.minShelfLifeRemainingMonths != null || line.maxDateCodeAgeMonths != null ||
    line.lotTraceabilityRequired || line.firstArticleInspectionRequired ||
    line.sourceInspectionRequired
  );
}

/**
 * Money with its currency code. Never render a bare effective-cost number: the
 * drawer used to print `3150` for a EUR line, indistinguishable from a USD
 * figure, right next to Compare's correctly-converted totals (R5).
 */
function fmtCost(n, currency) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(2)} ${currency || ''}`.trim();
}

function blank(line, baseCurrency = 'USD') {
  return {
    rfqLineItemId: line._id,
    quotedPartNumber: line.partNumber,
    qtyRequested: line.quantity,
    qtyQuoted: line.quantity,
    // Default to the RFQ's own currency so the common case needs no FX rate.
    currency: baseCurrency,
    fxRate: '',
    unitPrice: '',
    moq: '',
    packSize: '',
    leadTimeValue: '',
    leadTimeUnit: 'Weeks',
    leadTimeType: 'ExStock',
    manufacturerQuoted: '',
    // Conservative defaults. A blank form must not pre-assert compliance the
    // supplier has not claimed: 'FactoryNew' is the TOP of CONDITION_RANK and
    // 'Yes'/true here suppressed the QPL and CoC rules outright, so a clerk
    // keying a phone quote recorded unverified claims as fact. The schema's own
    // defaults are 'Unknown'/false for exactly this reason, and every aerospace
    // tri-state below already follows that.
    conditionQuoted: '',
    qplCompliant: 'Unknown',
    cocAvailable: false,
    cocType: 'Unknown',
    cocCovers: 'Original',
    // Per-line valid-until (#14). Blank means "use the envelope's date, if any".
    validUntil: '',
    // Landed cost inputs (#13) and aerospace answers (#16).
    countryOfOrigin: '',
    weightKg: '',
    dfarsCompliant: 'Unknown',
    specialtyMetalsCompliant: 'Unknown',
    meltCountry: '',
    itarEligible: 'Unknown',
    shelfLifeRemainingMonths: '',
    dateCode: '',
    dateCodeAgeMonths: '',
    lotTraceable: 'Unknown',
    lotNumber: '',
    firstArticleAvailable: 'Unknown',
    sourceInspectionAccepted: 'Unknown',
    notes: '',
  };
}

// Numeric fields render as '' when absent so the input stays empty rather than
// showing a spurious 0 (which would then be saved as a real quoted value).
function num(v) {
  return v == null ? '' : v;
}

/**
 * Seed the form from the saved quote line when one exists.
 *
 * Previously the drawer always rendered blank(line) and never merged the
 * fetched rows in, so reopening "Enter quote" showed hardcoded defaults and
 * saving pushed them over the stored quote — blanking the unit price to 0.
 */
function fromExisting(line, saved, baseCurrency = 'USD') {
  if (!saved) return blank(line, baseCurrency);
  const b = blank(line, baseCurrency);
  return {
    ...b,
    rfqLineItemId: line._id,
    quotedPartNumber: saved.quotedPartNumber ?? b.quotedPartNumber,
    qtyRequested: num(saved.qtyRequested) || line.quantity,
    qtyQuoted: num(saved.qtyQuoted),
    unitPrice: num(saved.unitPrice),
    currency: saved.currency || b.currency,
    fxRate: num(saved.fxRate),
    moq: num(saved.moq),
    packSize: num(saved.packSize),
    leadTimeValue: num(saved.leadTimeValue),
    leadTimeUnit: saved.leadTimeUnit ?? b.leadTimeUnit,
    leadTimeType: saved.leadTimeType ?? b.leadTimeType,
    manufacturerQuoted: saved.manufacturerQuoted ?? '',
    conditionQuoted: saved.conditionQuoted ?? b.conditionQuoted,
    qplCompliant: saved.qplCompliant ?? b.qplCompliant,
    cocAvailable: saved.cocAvailable ?? b.cocAvailable,
    cocType: saved.cocType ?? b.cocType,
    cocCovers: saved.cocCovers ?? b.cocCovers,
    validUntil: saved.validUntil ? String(saved.validUntil).slice(0, 10) : '',
    countryOfOrigin: saved.countryOfOrigin ?? '',
    weightKg: num(saved.weightKg),
    dfarsCompliant: saved.dfarsCompliant ?? 'Unknown',
    specialtyMetalsCompliant: saved.specialtyMetalsCompliant ?? 'Unknown',
    meltCountry: saved.meltCountry ?? '',
    itarEligible: saved.itarEligible ?? 'Unknown',
    shelfLifeRemainingMonths: num(saved.shelfLifeRemainingMonths),
    dateCode: saved.dateCode ?? '',
    dateCodeAgeMonths: num(saved.dateCodeAgeMonths),
    lotTraceable: saved.lotTraceable ?? 'Unknown',
    lotNumber: saved.lotNumber ?? '',
    firstArticleAvailable: saved.firstArticleAvailable ?? 'Unknown',
    sourceInspectionAccepted: saved.sourceInspectionAccepted ?? 'Unknown',
    notes: saved.notes ?? '',
  };
}

export default function QuoteCaptureDrawer({ envelope, lines, onClose, baseCurrency = 'USD' }) {
  const toast = useToast();
  const ask = useAsk();
  const [quotes, setQuotes] = useState([]);
  const [existing, setExisting] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    // Render blanks first so the drawer isn't empty while the fetch is in
    // flight, then re-seed from the saved rows as soon as they arrive.
    setQuotes(lines.map((l) => blank(l, baseCurrency)));
    api
      .listQuoteLines(envelope._id)
      .then((r) => {
        if (cancelled) return;
        const items = r.items || [];
        setExisting(items);
        setQuotes(
          lines.map((l) =>
            fromExisting(
              l,
              items.find((x) => String(x.rfqLineItemId) === String(l._id)),
              baseCurrency
            )
          )
        );
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [envelope._id, lines, baseCurrency]);

  function set(i, k, v) {
    setQuotes((q) => q.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  }

  async function saveRow(i) {
    const q = quotes[i];
    const existingLine = existing.find((x) => String(x.rfqLineItemId) === String(q.rfqLineItemId));

    // Guard the data-loss path: an empty price must never be written as 0.
    // Previously `Number('') || 0` silently zeroed a stored quote on re-save.
    if (q.unitPrice === '' || q.unitPrice == null) {
      if (existingLine && existingLine.unitPrice != null) {
        toast.push(
          `Unit price is empty — refusing to overwrite the saved price of ${existingLine.unitPrice}. Enter a price or close without saving.`,
          { variant: 'error' }
        );
      } else {
        toast.push('Enter a unit price before saving this line.', { variant: 'error' });
      }
      return;
    }
    if (q.qtyQuoted === '' || Number(q.qtyQuoted) <= 0) {
      toast.push('Quantity quoted must be greater than 0.', { variant: 'error' });
      return;
    }
    // A foreign-currency quote without a rate would be excluded from Compare, so
    // warn at entry rather than letting it silently drop out of the matrix.
    const needsRate = q.currency && q.currency !== baseCurrency;
    if (needsRate && !(Number(q.fxRate) > 0)) {
      // In-app dialog. With window.confirm this was the worst instance of the R1
      // pattern: once the browser suppressed dialogs, confirm() returned false and
      // the handler returned with NO toast at all — the buyer saw the Save button
      // do nothing and reasonably concluded the quote had saved.
      const proceed = await ask({
        title: `No ${baseCurrency} exchange rate`,
        body:
          `This quote is in ${q.currency} with no exchange rate to ${baseCurrency}.\n\n` +
          'It will be saved, but excluded from totals, landed cost and best-price ranking ' +
          'on the Compare tab until a rate is entered.',
        confirmLabel: 'Save without a rate',
      });
      if (!proceed) {
        toast.push('Not saved — enter an exchange rate, or confirm saving without one.');
        return;
      }
    }

    setSaving((s) => ({ ...s, [i]: true }));
    try {
      const body = {
        ...q,
        unitPrice: Number(q.unitPrice),
        qtyRequested: Number(q.qtyRequested) || 0,
        qtyQuoted: Number(q.qtyQuoted),
        moq: q.moq === '' ? undefined : Number(q.moq),
        packSize: q.packSize === '' ? undefined : Number(q.packSize),
        leadTimeValue: q.leadTimeValue === '' ? undefined : Number(q.leadTimeValue),
        currency: q.currency || baseCurrency,
        // Blank means "supplier did not state one". Must be undefined, not '',
        // because the schema enum has no empty member and would reject it.
        conditionQuoted: q.conditionQuoted || undefined,
        fxRate: q.fxRate === '' ? undefined : Number(q.fxRate),
        validUntil: q.validUntil || undefined,
        // Landed cost (#13) + aerospace answers (#16). Blank numerics stay
        // undefined so "not stated" is distinct from zero.
        countryOfOrigin: q.countryOfOrigin ? q.countryOfOrigin.toUpperCase() : undefined,
        weightKg: q.weightKg === '' ? undefined : Number(q.weightKg),
        meltCountry: q.meltCountry ? q.meltCountry.toUpperCase() : undefined,
        shelfLifeRemainingMonths: q.shelfLifeRemainingMonths === '' ? undefined : Number(q.shelfLifeRemainingMonths),
        dateCodeAgeMonths: q.dateCodeAgeMonths === '' ? undefined : Number(q.dateCodeAgeMonths),
        dateCode: q.dateCode || undefined,
        lotNumber: q.lotNumber || undefined,
      };
      let saved;
      if (existingLine) {
        saved = await api.patchQuoteLine(existingLine._id, body);
      } else {
        saved = await api.createQuoteLine(envelope._id, body);
      }
      // An update now commits a NEW revision, so `saved._id` differs from the
      // row we patched. Reconcile by rfqLineItemId — matching on _id appended a
      // duplicate entry for the same line on every edit.
      setExisting((e) => {
        const idx = e.findIndex((x) => String(x.rfqLineItemId) === String(saved.rfqLineItemId));
        if (idx === -1) return [...e, saved];
        const copy = e.slice(); copy[idx] = saved; return copy;
      });
      toast.push(
        saved.revision > 1
          ? `Quote saved as revision ${saved.revision} (${saved.qualityStatus})`
          : `Quote saved (${saved.qualityStatus})`
      );
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setSaving((s) => ({ ...s, [i]: false }));
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,30,0.4)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 720, background: '#fff', boxShadow: '0 0 27px rgba(0,0,0,0.3)', overflowY: 'auto' }}>
        <div style={{ padding: 20, borderBottom: '1px solid var(--border-1)' }}>
          <h2 className="card-title">Quote from {envelope.supplier?.name}</h2>
          <div className="subtitle">{envelope.coveragePct}% covered · {envelope.quoteCompleteness}</div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onClose}>Close</button>
        </div>
        <div style={{ padding: 20 }}>
          {lines.map((line, i) => {
            const existingLine = existing.find((x) => String(x.rfqLineItemId) === String(line._id));
            const q = quotes[i];
            if (!q) return null;
            return (
              <div key={line._id} className="card" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <b>Line {line.lineNumber}</b> — <span className="mono">{line.partNumber}</span> · Qty {line.quantity}
                  </div>
                  {existingLine && (
                    <span className={'chip ' + (existingLine.qualityStatus === 'QualityReady' ? 'ready' : existingLine.qualityStatus === 'ReviewRequired' ? 'review' : 'risk')}>
                      {existingLine.qualityStatus}
                    </span>
                  )}
                </div>
                <div className="row" style={{ marginTop: 8 }}>
                  <label className="field"><span className="label">Quoted Part #</span>
                    <input className="input mono" value={q.quotedPartNumber} onChange={(e) => set(i, 'quotedPartNumber', e.target.value)} />
                  </label>
                  <label className="field"><span className="label">Qty quoted</span>
                    <input className="input mono" type="number" value={q.qtyQuoted} onChange={(e) => set(i, 'qtyQuoted', e.target.value)} />
                  </label>
                  <label className="field"><span className="label">Unit price</span>
                    <input className="input mono" type="number" step="0.01" aria-label="Unit price" value={q.unitPrice} onChange={(e) => set(i, 'unitPrice', e.target.value)} />
                  </label>
                </div>
                {/* Currency (#09). Quotes are compared in the RFQ's currency, so
                    a foreign-currency quote needs a rate — we never assume 1:1. */}
                <div className="row">
                  <label className="field"><span className="label">Currency</span>
                    <select className="select" aria-label="Currency" value={q.currency} onChange={(e) => set(i, 'currency', e.target.value)}>
                      {[...new Set([baseCurrency, ...CURRENCIES])].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  {q.currency !== baseCurrency ? (
                    <label className="field">
                      <span className="label">Rate → {baseCurrency}</span>
                      <input
                        className="input mono"
                        type="number"
                        step="0.0001"
                        aria-label={`Exchange rate to ${baseCurrency}`}
                        placeholder={`1 ${q.currency} = ? ${baseCurrency}`}
                        value={q.fxRate}
                        onChange={(e) => set(i, 'fxRate', e.target.value)}
                      />
                    </label>
                  ) : (
                    <div className="field">
                      <span className="label">Rate</span>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-500)', paddingTop: 8 }}>
                        Same as RFQ currency — no conversion
                      </div>
                    </div>
                  )}
                  <div className="field">
                    <span className="label">In {baseCurrency}</span>
                    <div className="mono" style={{ fontSize: 12, paddingTop: 8 }}>
                      {q.currency === baseCurrency
                        ? `${Number(q.unitPrice || 0).toFixed(2)} / unit`
                        : Number(q.fxRate) > 0 && q.unitPrice !== ''
                        ? `${(Number(q.unitPrice) * Number(q.fxRate)).toFixed(2)} / unit`
                        : <span style={{ color: 'var(--danger, #C5283D)' }}>rate needed</span>}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <label className="field"><span className="label">MOQ</span>
                    <input className="input mono" type="number" value={q.moq} onChange={(e) => set(i, 'moq', e.target.value)} />
                  </label>
                  <label className="field"><span className="label">Pack size</span>
                    <input className="input mono" type="number" value={q.packSize} onChange={(e) => set(i, 'packSize', e.target.value)} />
                  </label>
                  <label className="field"><span className="label">Manufacturer</span>
                    <input className="input" value={q.manufacturerQuoted} onChange={(e) => set(i, 'manufacturerQuoted', e.target.value)} />
                  </label>
                </div>
                <div className="row">
                  <label className="field"><span className="label">Condition</span>
                    {/* A blank first option so an unstated condition stays
                        unstated. 'Unknown' is not in the schema enum, so the
                        empty value is how "not told" is represented; the quality
                        engine already scores an absent condition as Review. */}
                    <select className="select" value={q.conditionQuoted} onChange={(e) => set(i, 'conditionQuoted', e.target.value)}>
                      <option value="">— not stated —</option>
                      {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="field"><span className="label">QPL</span>
                    <select className="select" value={q.qplCompliant} onChange={(e) => set(i, 'qplCompliant', e.target.value)}>
                      {['Yes', 'No', 'Unknown'].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="field"><span className="label">CoC avail</span>
                    <select className="select" value={q.cocAvailable ? 'Yes' : 'No'} onChange={(e) => set(i, 'cocAvailable', e.target.value === 'Yes')}>
                      <option>Yes</option><option>No</option>
                    </select>
                  </label>
                </div>
                <div className="row">
                  <label className="field"><span className="label">CoC type</span>
                    <select className="select" value={q.cocType} onChange={(e) => set(i, 'cocType', e.target.value)}>
                      {COC_TYPES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="field"><span className="label">CoC covers</span>
                    <select className="select" value={q.cocCovers} onChange={(e) => set(i, 'cocCovers', e.target.value)}>
                      {['Original', 'Alternate', 'Both', 'Unknown'].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  {/* Three controls share this label, so each carries its own
                      aria-label — otherwise none of them has a usable name. */}
                  <div className="field"><span className="label">Lead time</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="input mono" type="number" aria-label="Lead time value" value={q.leadTimeValue} onChange={(e) => set(i, 'leadTimeValue', e.target.value)} style={{ width: 80 }} />
                      <select className="select" aria-label="Lead time unit" value={q.leadTimeUnit} onChange={(e) => set(i, 'leadTimeUnit', e.target.value)} style={{ width: 90 }}>
                        <option>Days</option><option>Weeks</option>
                      </select>
                      <select className="select" aria-label="Lead time type" value={q.leadTimeType} onChange={(e) => set(i, 'leadTimeType', e.target.value)}>
                        {LT_TYPES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  {/* Price validity (#14). Awarding a lapsed price triggers an
                      unplanned repricing conversation, so capture it up front. */}
                  <label className="field"><span className="label">Price valid until</span>
                    <input
                      className="input mono"
                      type="date"
                      aria-label="Price valid until"
                      value={q.validUntil}
                      onChange={(e) => set(i, 'validUntil', e.target.value)}
                    />
                  </label>
                  {/* Origin + weight drive the landed-cost lane (#13). */}
                  <label className="field"><span className="label">Country of origin</span>
                    <input className="input mono" aria-label="Country of origin" maxLength={2}
                      placeholder="US" value={q.countryOfOrigin}
                      onChange={(e) => set(i, 'countryOfOrigin', e.target.value.toUpperCase())} />
                  </label>
                  <label className="field"><span className="label">Weight (kg)</span>
                    <input className="input mono" type="number" step="0.1" aria-label="Weight kg"
                      value={q.weightKg} onChange={(e) => set(i, 'weightKg', e.target.value)} />
                  </label>
                </div>

                {/* Aerospace answers (#16) — only for lines that actually impose a
                    requirement, so the price isn't buried under questions nobody
                    asked. */}
                {needsAero(line) && (
                  <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-1)', borderRadius: 8 }}>
                    <b style={{ fontSize: 12 }}>Aerospace requirements on this line</b>
                    <div className="row" style={{ marginTop: 6 }}>
                      {line.dfarsRequired && (
                        <label className="field"><span className="label">DFARS compliant</span>
                          <select className="select" aria-label="DFARS compliant" value={q.dfarsCompliant}
                            onChange={(e) => set(i, 'dfarsCompliant', e.target.value)}>
                            {TRI.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </label>
                      )}
                      {line.specialtyMetalsCompliance && (
                        <>
                          <label className="field"><span className="label">Specialty metals</span>
                            <select className="select" aria-label="Specialty metals compliant" value={q.specialtyMetalsCompliant}
                              onChange={(e) => set(i, 'specialtyMetalsCompliant', e.target.value)}>
                              {TRI.map((t) => <option key={t}>{t}</option>)}
                            </select>
                          </label>
                          <label className="field"><span className="label">Melt country</span>
                            <input className="input mono" aria-label="Melt country" maxLength={2}
                              value={q.meltCountry} onChange={(e) => set(i, 'meltCountry', e.target.value.toUpperCase())} />
                          </label>
                        </>
                      )}
                      {line.itarControlled && (
                        <label className="field"><span className="label">ITAR eligible</span>
                          <select className="select" aria-label="ITAR eligible" value={q.itarEligible}
                            onChange={(e) => set(i, 'itarEligible', e.target.value)}>
                            {TRI.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                    <div className="row">
                      {line.minShelfLifeRemainingMonths != null && (
                        <label className="field">
                          <span className="label">Shelf life left (mo) · need {line.minShelfLifeRemainingMonths}</span>
                          <input className="input mono" type="number" aria-label="Shelf life remaining months"
                            value={q.shelfLifeRemainingMonths}
                            onChange={(e) => set(i, 'shelfLifeRemainingMonths', e.target.value)} />
                        </label>
                      )}
                      {line.maxDateCodeAgeMonths != null && (
                        <>
                          <label className="field"><span className="label">Date code</span>
                            <input className="input mono" aria-label="Date code" value={q.dateCode}
                              onChange={(e) => set(i, 'dateCode', e.target.value)} />
                          </label>
                          <label className="field">
                            <span className="label">Age (mo) · max {line.maxDateCodeAgeMonths}</span>
                            <input className="input mono" type="number" aria-label="Date code age months"
                              value={q.dateCodeAgeMonths}
                              onChange={(e) => set(i, 'dateCodeAgeMonths', e.target.value)} />
                          </label>
                        </>
                      )}
                    </div>
                    <div className="row">
                      {line.lotTraceabilityRequired && (
                        <>
                          <label className="field"><span className="label">Lot traceable</span>
                            <select className="select" aria-label="Lot traceable" value={q.lotTraceable}
                              onChange={(e) => set(i, 'lotTraceable', e.target.value)}>
                              {TRI.map((t) => <option key={t}>{t}</option>)}
                            </select>
                          </label>
                          <label className="field"><span className="label">Lot number</span>
                            <input className="input mono" aria-label="Lot number" value={q.lotNumber}
                              onChange={(e) => set(i, 'lotNumber', e.target.value)} />
                          </label>
                        </>
                      )}
                      {line.firstArticleInspectionRequired && (
                        <label className="field"><span className="label">First article available</span>
                          <select className="select" aria-label="First article available" value={q.firstArticleAvailable}
                            onChange={(e) => set(i, 'firstArticleAvailable', e.target.value)}>
                            {TRI.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </label>
                      )}
                      {line.sourceInspectionRequired && (
                        <label className="field"><span className="label">Source inspection accepted</span>
                          <select className="select" aria-label="Source inspection accepted" value={q.sourceInspectionAccepted}
                            onChange={(e) => set(i, 'sourceInspectionAccepted', e.target.value)}>
                            {TRI.map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                  </div>
                )}
                <div className="row">
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  {existingLine && (
                    /* Effective cost, labelled with the currency it is in, plus the
                       converted figure when the supplier quoted in another currency
                       (R5).

                       This printed a bare `effectiveTotalCost` with no currency at
                       all, so a EUR line read "EffCost: 3150" — the same shape as a
                       USD figure — while Compare's totals were correctly converted.
                       An unlabelled number next to correctly-converted totals is
                       worse than no number: it silently invites comparison. */
                    <span className="mono" style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                      EffCost: {fmtCost(existingLine.effectiveTotalCost, existingLine.currency)}
                      {existingLine.effectiveTotalCostBase != null &&
                        existingLine.currency &&
                        existingLine.currency !== baseCurrency && (
                          <>
                            {' '}= {fmtCost(existingLine.effectiveTotalCostBase, baseCurrency)}
                            {existingLine.fxRate ? ` @ ${existingLine.fxRate}` : ''}
                          </>
                        )}
                      {existingLine.isConvertible === false && (
                        <span style={{ color: 'var(--red-600, #D02020)' }}> · no rate, not compared</span>
                      )}
                      {' '}· ExcessQty: {existingLine.excessQty} · Confidence: {existingLine.leadTimeConfidence}
                    </span>
                  )}
                  <div style={{ flex: 1 }} />
                  {/* Disabled until the saved rows land, so a fast click can't
                      save blank defaults over a stored quote. */}
                  <button
                    className="btn btn-sm"
                    onClick={() => saveRow(i)}
                    disabled={saving[i] || !loaded}
                    title={!loaded ? 'Loading saved quote…' : undefined}
                  >
                    {saving[i] ? 'Saving…' : !loaded ? 'Loading…' : existingLine ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
